import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { ThemeProvider } from "./hooks/useTheme";
import Dashboard from "./pages/Dashboard";
import Auth from "./pages/Auth";
import Landing from "./pages/Landing";
const PlayerProfile = lazy(() => import("./pages/PlayerProfile"));
const Profile = lazy(() => import("./pages/Profile"));
const ScoutingSession = lazy(() => import("./pages/ScoutingSession"));
const AddPlayer = lazy(() => import("./pages/AddPlayer"));
const ScoutPlayer = lazy(() => import("./pages/ScoutPlayer"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Badges = lazy(() => import("./pages/Badges"));
const PlayerCalibration = lazy(() => import("./pages/PlayerCalibration"));
import Settings from "./pages/Settings";

const RouteFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div role="status" aria-label="Loading" className="h-8 w-8 rounded-full border-[3px] border-primary/20 border-t-primary motion-safe:animate-spin motion-reduce:animate-pulse"></div>
  </div>
);

const queryClient = new QueryClient();

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div role="status" aria-label="Loading" className="h-8 w-8 rounded-full border-[3px] border-primary/20 border-t-primary motion-safe:animate-spin motion-reduce:animate-pulse"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div role="status" aria-label="Loading" className="h-8 w-8 rounded-full border-[3px] border-primary/20 border-t-primary motion-safe:animate-spin motion-reduce:animate-pulse"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
}

function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div role="status" aria-label="Loading" className="h-8 w-8 rounded-full border-[3px] border-primary/20 border-t-primary motion-safe:animate-spin motion-reduce:animate-pulse"></div>
      </div>
    );
  }
  return user ? <Dashboard /> : <Landing />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AuthProvider>
          <BrowserRouter>
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<HomeRoute />} />
              <Route path="/auth" element={
                <PublicRoute>
                  <Auth />
                </PublicRoute>
              } />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/badges" element={<Badges />} />
              <Route path="/player/:id" element={
                <ProtectedRoute>
                  <PlayerProfile />
                </ProtectedRoute>
              } />
              <Route path="/scout/:id" element={
                <ProtectedRoute>
                  <ScoutingSession />
                </ProtectedRoute>
              } />
              <Route path="/scout" element={
                <ProtectedRoute>
                  <ScoutPlayer />
                </ProtectedRoute>
              } />
              <Route path="/add-player" element={
                <ProtectedRoute>
                  <AddPlayer />
                </ProtectedRoute>
              } />
              <Route path="/calibrate/:id" element={
                <ProtectedRoute>
                  <PlayerCalibration />
                </ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <ProtectedRoute>
                  <AdminPanel />
                </ProtectedRoute>
              } />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
